import { useEffect, useMemo, useRef, useState } from 'react';
import { api, fetchFinInstFileUrl, uploadFinInstFile } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, moneyInput, useList, type Column, type Row, IdCell, MoneyInput } from '../components/DataTable';
import { RefPicker } from './MasterForm';
import { canScreen, useMe } from '../lib/me';
import FinInstExpirePanel from '../components/FinInstExpirePanel';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';
import { useUrlSearch } from '../lib/deepLink';
import { FinInstPayView, PayForm, PaySummary } from './FinInstPay';
import { AllocFileChips } from '../components/AllocChangeAlerts';
import { useStickyDriveMon } from '../lib/driveMon';

/** #508 기억된 정산년월이 없을 때 — 6일부터 그 달, 1~5일은 전월(재무추가 새 항목과 같은 식). */
function defaultMon(): string {
  const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
  if (Number(today.slice(8, 10)) > 5) return today.slice(0, 7);
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * 할부금 — 차량 할부 계약과 **회차**(레거시 `FinInst*`).
 *
 * 회차 한 줄 = 한 달치 공제이고, 그게 그대로 **급여명세의 할부금 섹션**이 된다.
 *
 * ⚠️ 계약을 등록하면 **회차가 함께 전개된다**(시작월부터 상환월수만큼).
 *    첫 회차는 첫 회차 금액, 마지막은 마지막회차 금액, 나머지는 분납액.
 * ⚠️ 전개 범위에 **정산완료월이 들어가면 거부**한다 — 이미 명세가 나간 달에 공제를 끼워 넣을 수 없다.
 * ⚠️ 계약을 지우면 남은 회차도 같이 내려간다. 다만 **마감된 달의 회차는 남긴다**(원장과 어긋나면 안 된다).
 */


/**
 * 상태 탭 — 종료 = 마지막 회차월이 지난 계약(서버 판정).
 * 회계·관리자에겐 회사 실납부 탭 둘이 더 붙는다(10-27 way — 따로 있던 「회사 실납부」 화면을 이 목록으로 합침):
 *   paydiff = 이번 달 회사 납부 ≠ 지난달 공제(서버) · payonly = 공제 계약이 없는 실납부(직영·회사할부 등 — 이 목록엔 줄이 없다).
 */
type FinScope = 'active' | 'repay' | 'done' | 'all' | 'paydiff' | 'payonly';
const FIN_SCOPES: readonly (readonly [FinScope, string])[] = [
  ['active', '진행중'], ['repay', '중도상환'], ['done', '종료'], ['all', '전체'],
];
const PAY_SCOPES: readonly (readonly [FinScope, string])[] = [
  ['paydiff', '회사 납부 ≠ 공제'], ['payonly', '회사 납부만(공제 없음)'],
];

export default function FinInst() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'fin-inst');

  const [scope, setScope] = useState<FinScope>('active');
  const [corpId, setCorpId] = useState<number | null>(null);
  const [corps, setCorps] = useState<{ corp_id: number; short_name: string }[]>([]);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('fin-inst', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  // 다른 화면에서 건너온 링크(?q=·?mon= — 정산현황 줄 보기 · lib/deepLink.ts)면 그 검색어로.
  useUrlSearch(setTerm, setQ);
  const [nonce, setNonce] = useState(0);
  const [detail, setDetail] = useState<number | null>(null);
  const [editing, setEditing] = useState<'new' | null>(null);
  const [bulkNew, setBulkNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 회사 실납부(10-01 미팅 #30) — 회계·관리자만(서버가 `showPay` 로 알려 준다). */
  const [showPay, setShowPay] = useState(false);
  const [payNew, setPayNew] = useState(false);
  /** #449 할부금 만기 구글챗 알림 창 — 회계·관리자(showPay)만. */
  const [expireOpen, setExpireOpen] = useState(false);
  const payOnly = scope === 'payonly';
  // #508 정산년월 — 차량·차주·기사 칸과 이름 검색이 그 달 회차에 적용된 사람 기준(다른 화면과 같은 기억값).
  const [stickyMon, setStickyMon] = useStickyDriveMon();
  const mon = stickyMon || defaultMon();
  const [past, setPast] = useState(false);

  const { rows, loading } = useList(
    () => api.listFinInst({ scope: payOnly ? 'active' : scope, corpId, q: q || undefined, mon, past, limit: PAGE, offset: page * PAGE })
      .then((r) => { if (r.corps) setCorps(r.corps); setShowPay(!!r.showPay); return r; }) as unknown as Promise<{ rows: Row[] }>,
    [scope, corpId, q, mon, past, page, PAGE, nonce],
  );

  const columns: Column[] = [
    // phone: 폰에선 차주를 왼쪽에 고정하고 번호·법인·ID·기사·상환월수·회차 합계는 뺀다(DataTable `phone` · 2026-09-30 way).
    { key: 'fin_inst_id', label: '번호', align: 'right', phone: 'hide' },
    { key: 'corp_name', label: '법인', phone: 'hide' },
    { key: 'car_alloc_id', label: '편성 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'driver_id', label: '기사 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.driver_id} /> },
    { key: 'owner_id', label: '차주 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.owner_id} /> },
    { key: 'car_no', label: '차량' },
    { key: 'owner_name', label: '차주', phone: 'pin' },
    { key: 'driver_name', label: '기사', phone: 'hide',
      render: (r) => <span title={r.cur_from_amt ? `${mon} 회차에 적용된 기사` : `${mon} 회차가 없어 계약에 적힌 기사`}>{dash(r.driver_name)}</span> },
    // #508 — 회차마다 기사가 바뀐 계약만 처음 나온 달 순으로(기사 한 명이면 빈칸).
    { key: 'driver_hist', label: '기사 이력', phone: 'hide', noSort: true,
      render: (r) => <span className="text-xs">{dash(r.driver_hist)}</span> },
    { key: 'company', label: '할부금계정' },
    // #254 — 이율은 목록에서도 같이 보이게(정렬은 서버 목록 순서 그대로).
    { key: 'interest', label: '이율', align: 'right', noSort: true, render: (r) => rateOf(r.interest) },
    { key: 'start_drive_mon', label: '시작월' },
    { key: 'month_cnt', label: '상환월수', align: 'right', phone: 'hide' },
    { key: 'month_amount', label: '분납액', align: 'right', render: (r) => money(r.month_amount) },
    {
      key: 'amt_cnt', label: '회차', align: 'right',
      // 전개된 회차 수가 상환월수와 다르면 그 사실을 보여준다 — 중간에 건너뛴 달이 있다는 뜻이다.
      render: (r) => {
        const differs = Number(r.amt_cnt) !== Number(r.month_cnt);
        return (
          <span className={differs ? 'text-amber-700' : ''} title={differs ? `상환월수 ${r.month_cnt}` : undefined}>
            {String(r.amt_cnt)}{differs && ' ⚠'}
          </span>
        );
      },
    },
    { key: 'amt_sum', label: '회차 합계', align: 'right', phone: 'hide', render: (r) => money(r.amt_sum) },
    { key: 'last_mon', label: '마지막 회차월' },
    // 회사 실납부(10-01 미팅 #30) — 이번 달 납부 ↔ 지난달 공제. 회계·관리자에게만 내려온다.
    ...(showPay ? [
      { key: 'pay_this', label: '이번 달 회사 납부', align: 'right', phone: 'hide',
        render: (r: Row) => (Number(r.pay_cnt ?? 0) === 0 ? <span className="text-xs text-ink-faint">연결 없음</span> : money(r.pay_this ?? 0)) },
      { key: 'ded_prev', label: '차이(납부−지난달 공제)', align: 'right', phone: 'hide',
        render: (r: Row) => {
          if (Number(r.pay_cnt ?? 0) === 0 || r.pay_this == null) return '';
          const d = Number(r.pay_this) - Number(r.ded_prev ?? 0);
          return Math.abs(d) < 1 ? <span className="text-emerald-700">같음</span>
            : <b className={d > 0 ? 'text-red-600' : 'text-amber-700'}>{d > 0 ? '+' : ''}{money(String(d))}</b>;
        } },
      { key: 'pay_reason', label: '차이 사유', phone: 'hide', noSort: true,
        render: (r: Row) => <span className="text-xs">{dash(r.pay_reason)}</span> },
    ] as Column[] : []),
    {
      key: 'state', label: '상태',
      // 판정은 서버(`FIN_STATE_SQL`) 하나 — 탭과 이 칸이 같은 값을 본다.
      render: (r) =>
        r.state === 'repay' ? <span className="text-amber-700">중도상환</span>
        : r.state === 'done' ? <span className="text-ink-faint">종료</span>
        : <span className="text-emerald-700">진행중</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="page-head">
        <h1 className="font-bold">할부금</h1>
        <p className="mt-1 text-sm text-ink-soft">
          차량 할부 계약과 회차입니다. <b>회차 한 줄이 그 달 급여명세의 할부금 공제</b>가 됩니다.
          계약을 등록하면 시작월부터 상환월수만큼 회차가 함께 만들어집니다.
        </p>
      </div>

      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {([[null, '전체 법인'], ...corps.map((c) => [c.corp_id, c.short_name] as const)] as const).map(([id, label]) => (
            <button key={String(id)} onClick={() => { setCorpId(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      corpId === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <span className="h-5 w-px bg-line" aria-hidden />
        <div className="flex gap-1">
          {[...FIN_SCOPES, ...(showPay ? PAY_SCOPES : [])].map(([id, label]) => (
            <button key={id} onClick={() => { setScope(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      scope === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        {!payOnly && (
          <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}
                className="flex items-center gap-2">
            <input className="field w-56" placeholder="차량번호·차주·기사·할부금계정"
                   value={term} onChange={(e) => setTerm(e.target.value)} />
            <button className="btn-search" type="submit" aria-label="검색" title="검색" />
          </form>
        )}
        {!payOnly && (
          <>
            <label className="flex items-center gap-1 text-sm" title="차량·차주·기사 칸과 이름 검색이 이 달 회차에 적용된 사람 기준입니다">
              정산년월
              <input className="field w-36" type="month" value={mon}
                     onChange={(e) => { if (e.target.value) { setStickyMon(e.target.value); setPage(0); } }} />
            </label>
            <label className="flex items-center gap-1 text-sm" title="체크하면 이 계약에 예전에 적용됐던 기사·차주·차량으로도 찾습니다">
              <input type="checkbox" checked={past} onChange={(e) => { setPast(e.target.checked); setPage(0); }} />
              과거 기사 포함
            </label>
          </>
        )}
        {showPay && !canEdit && (
          <button className="btn-ghost ml-auto" onClick={() => setExpireOpen(true)}>만기 알림</button>
        )}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            {showPay && <button className="btn-ghost" onClick={() => setExpireOpen(true)}>만기 알림</button>}
            {showPay && <button className="btn-ghost" onClick={() => setPayNew(true)}>회사 실납부 추가</button>}
            <button className="btn-ghost" onClick={() => { setBulkNew(true); setDetail(null); }}>대량 등록</button>
            <button className="btn" onClick={() => { setEditing('new'); setDetail(null); }}>새 계약</button>
          </div>
        )}
      </div>

      {expireOpen && (
        <Modal width="max-w-4xl">
          <FinInstExpirePanel isAdmin={me?.role === 'admin'} onClose={() => setExpireOpen(false)} />
        </Modal>
      )}

      {payNew && (
        <Modal width="max-w-4xl">
          <PayForm row={null} canEdit={canEdit} onClose={() => setPayNew(false)}
                   onSaved={() => { setPayNew(false); setNonce((n) => n + 1); }} />
        </Modal>
      )}

      {error && <ErrorBox message={error} />}

      {bulkNew && (
        <Modal width="max-w-5xl">
          <FinInstBulkForm
            onClose={() => setBulkNew(false)}
            onSaved={() => { setBulkNew(false); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {editing === 'new' && (
        <Modal width="max-w-4xl">
          <FinInstForm
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      {detail !== null && (
        <Modal width="max-w-5xl">
          <FinInstDetail finInstId={detail} canEdit={canEdit}
                         onClose={() => setDetail(null)}
                         onChanged={() => setNonce((n) => n + 1)} />
        </Modal>
      )}

      {payOnly ? <FinInstPayView canEdit={canEdit} key={nonce} /> : (
        <>
          <DataTable tableId="finInst.contracts" columns={columns} rows={rows} loading={loading}
                     onRowClick={(r) => setDetail((c) => (c === Number(r.fin_inst_id) ? null : Number(r.fin_inst_id)))}
                     activeRow={(r) => detail === Number(r.fin_inst_id)} />
          <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
        </>
      )}
    </div>
  );
}

/** 계약 상세 — 회차 목록 + 변경이력. 회차 금액은 여기서 고친다(마감월은 서버가 막는다). */
/** 금액 문자열(소수 4자리까지) 합 — 소수점 오차 없이 1/10000 단위 정수로 더한다. */
function sumAmounts(vals: unknown[]): string {
  let t = 0n;
  for (const v of vals) {
    const m = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(String(v ?? '').replace(/,/g, '').trim());
    if (!m) continue;
    const n = BigInt(m[2]!) * 10000n + BigInt((m[3] ?? '').padEnd(4, '0') || '0');
    t += m[1] ? -n : n;
  }
  const neg = t < 0n; const a = neg ? -t : t;
  const frac = (a % 10000n).toString().padStart(4, '0').replace(/0+$/, '');
  return `${neg ? '-' : ''}${a / 10000n}${frac ? `.${frac}` : ''}`;
}

/** 이율(%) 표시 — 비면 「—」. */
function rateOf(v: unknown): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${n}%` : '—';
}

function FinInstDetail({ finInstId, canEdit, onClose, onChanged }: {
  finInstId: number; canEdit: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [nonce, setNonce] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { rows: _r, loading } = useList(async (alive) => {
    const d = await api.getFinInst({ finInstId });
    if (alive()) setData(d);
    return { rows: [] as Row[] };
  }, [finInstId, nonce]);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getFinInst>> | null>(null);
  void _r;

  // 회차 선택 — 일괄 조작 3종의 대상. 목록이 새로 오면 비운다(사라진 행을 들고 있으면 서버가 거부한다).
  const [sel, setSel] = useState<number[]>([]);
  const [bulk, setBulk] = useState<null | 'amt' | 'alloc' | 'owner' | 'month' | 'comment'>(null);
  /** #339 계약 수정 창(할부사·금액 오타 · 중도상환날짜 · 납부일). */
  const [editOpen, setEditOpen] = useState(false);
  const selSet = new Set(sel);
  const amts = (data?.amts ?? []) as unknown as Row[];
  // #254 할부 총합계 — 그 계약 회차 **전부**가 내려오므로(쪽 나눔 없음) 화면에서 더해도 전체 합이다.
  const amtTotal = useMemo(() => sumAmounts(amts.map((r) => r.amt)), [amts]);
  const allIds = amts.map((a) => Number(a.fin_inst_amt_id));
  const allChecked = allIds.length > 0 && allIds.every((id) => selSet.has(id));

  function toggle(id: number) {
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }

  /**
   * 끌어 고르기(#203) — 출납일보(#38·#111)와 같은 손놀림.
   * 네모를 누르면 그 회차를 더하고(이미 골랐으면 뺀다) 누른 채 끌면 이어서 고른다 · 줄 아무 데서나 끌면 그 범위를 새로 고른다
   * · Shift = 앞서 누른 회차부터 여기까지 · Ctrl = 하나씩 더하기/빼기.
   */
  const drag = useRef<{ anchor: number | null; mode: 'none' | 'pending' | 'on'; base: number[] }>({ anchor: null, mode: 'none', base: [] });
  const [dragging, setDragging] = useState(false);
  const rangeIds = (a: number, b: number) => {
    let i = allIds.indexOf(a); let j = allIds.indexOf(b);
    if (i < 0 || j < 0) return [b];
    if (i > j) [i, j] = [j, i];
    return allIds.slice(i, j + 1);
  };
  const union = (...xs: number[][]) => [...new Set(xs.flat())];
  function rowDown(r: Row, e: React.MouseEvent) {
    if (!canEdit || e.button !== 0) return;
    const id = Number(r.fin_inst_amt_id);
    const t = e.target as HTMLElement;
    const grip = !!t.closest('[data-grip]');
    if (!grip && t.closest('input, button, select, a, textarea')) return;
    const d = drag.current;
    if (e.shiftKey && d.anchor != null) {
      setSel(union(e.ctrlKey || e.metaKey ? sel : [], rangeIds(d.anchor, id)));
      e.preventDefault();
      return;
    }
    if (e.ctrlKey || e.metaKey) { toggle(id); d.anchor = id; e.preventDefault(); return; }
    d.anchor = id;
    if (grip) {
      e.preventDefault();
      if (selSet.has(id)) { setSel(sel.filter((x) => x !== id)); d.mode = 'none'; return; }
      d.base = sel;
      setSel(union(sel, [id]));
      d.mode = 'on'; setDragging(true);
    } else { d.base = []; d.mode = 'pending'; }
  }
  function rowEnter(r: Row) {
    const d = drag.current;
    if (d.mode === 'none' || d.anchor == null) return;
    const id = Number(r.fin_inst_amt_id);
    if (d.mode === 'pending') {
      if (id === d.anchor) return;
      d.mode = 'on'; setDragging(true);
      window.getSelection()?.removeAllRanges();
    }
    setSel(union(d.base, rangeIds(d.anchor, id)));
  }
  useEffect(() => {
    const up = () => { drag.current.mode = 'none'; setDragging(false); };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  function reload() { setSel([]); setBulk(null); setNonce((n) => n + 1); onChanged(); }

  // 상환스케줄표(#230) — 상세에서는 바로 이 계약에 붙인다 · 내리기는 소프트(버킷 객체는 남는다).
  const [fileBusy, setFileBusy] = useState(false);
  async function addFiles(picked: File[]) {
    if (!picked.length) return;
    setFileBusy(true);
    setError(null);
    const failed: string[] = [];
    for (const f of picked) {
      try { await uploadFinInstFile(f, finInstId); } catch (e) { failed.push(`${f.name} — ${e instanceof Error ? e.message : String(e)}`); }
    }
    if (failed.length) setError(`올리지 못한 파일: ${failed.join(' / ')}`);
    setFileBusy(false);
    setNonce((n) => n + 1);
  }
  async function removeFile(fileId: number) {
    if (!window.confirm('이 상환스케줄표를 내릴까요?')) return;
    setFileBusy(true);
    setError(null);
    try { await api.deleteFinInstFile({ fileId }); setNonce((n) => n + 1); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setFileBusy(false); }
  }

  async function editAmt(a: Record<string, unknown>) {
    const next = window.prompt(`${a.drive_mon} ${a.sort}회차 금액`, moneyInput(a.amt));
    if (next === null) return;
    setError(null);
    try {
      await api.updateFinInstAmt({ finInstAmtId: Number(a.fin_inst_amt_id), amt: next });
      setNonce((n) => n + 1); onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  /**
   * **회차 번호 자체를 고친다**(2026-08-02 사용자 요청).
   * 정산년월을 옮기고 나면 번호와 달이 어긋난 채 남는데 지금까지 고칠 방법이 없었다.
   * ⚠️ 금액은 지금 값을 그대로 다시 보낸다 — 서버가 **전량 덮어쓰기**라 빼면 0 이 된다.
   */
  async function editSort(a: Record<string, unknown>) {
    const next = window.prompt(`${a.drive_mon} 의 회차 번호`, String(a.sort ?? ''));
    if (next === null) return;
    setError(null);
    try {
      await api.updateFinInstAmt({
        finInstAmtId: Number(a.fin_inst_amt_id),
        amt: String(a.amt ?? '0'),
        comment: a.comment == null ? undefined : String(a.comment),
        sort: next,
      });
      setNonce((n) => n + 1); onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  async function remove() {
    if (!window.confirm('계약을 삭제하면 남은 회차도 함께 내려갑니다.\n(정산이 끝난 달의 회차는 그대로 둡니다)\n계속할까요?')) return;
    setError(null);
    try {
      const r = await api.deleteFinInst({ finInstId });
      onChanged(); onClose();
      if (r.keptClosedMonths.length) {
        window.alert(`삭제했습니다. 정산완료월 ${r.keptClosedMonths.join(', ')} 의 회차는 남겼습니다.`);
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }

  const AMT_COLS: Column[] = [
    ...(canEdit ? [{
      key: '_sel',
      label: (
        <input type="checkbox" checked={allChecked} aria-label="회차 전체 선택"
               onChange={() => setSel(allChecked ? [] : allIds)} />
      ),
      // 진짜 체크박스 대신 네모 — 누름(mousedown)으로 고르고 끌기를 시작해야 해서 click 토글과 겹치면 두 번 바뀐다.
      render: (r: Row) => (
        <span data-grip role="checkbox" aria-checked={selSet.has(Number(r.fin_inst_amt_id))}
              aria-label={`${String(r.drive_mon ?? '')} 회차 선택`} title="눌러 고르기 · 끌면 여러 회차 · Shift·Ctrl 로 더하기"
              className={`inline-block h-3.5 w-3.5 cursor-pointer select-none rounded-sm border align-middle ${
                selSet.has(Number(r.fin_inst_amt_id)) ? 'border-sky-600 bg-sky-600' : 'border-ink-faint/60 bg-surface'}`} />
      ),
    } as Column] : []),
    {
      key: 'sort', label: '회차', align: 'right',
      // 번호를 눌러 바로 고친다 — 마감·지난 달은 서버가 막는다.
      render: (r) => (canEdit ? (
        <button className="underline decoration-dotted underline-offset-2 hover:text-brand-700"
                title="눌러서 회차 번호 수정"
                onClick={(e) => { e.stopPropagation(); void editSort(r as unknown as Record<string, unknown>); }}>
          {dash(r.sort)}
        </button>
      ) : dash(r.sort)),
    },
    { key: 'drive_mon', label: '정산월' },
    // ⚠️ 차주는 **회차마다 다를 수 있다** — 차주변경이 고른 회차만 바꾸기 때문이다(승계).
    //    계약 헤더의 차주 하나로 보여 주면 갈린 회차를 못 읽는다.
    { key: 'owner_name', label: '차주', phone: 'pin', render: (r) => dash(r.owner_name) },
    { key: 'driver_name', label: '기사', phone: 'hide', render: (r) => dash(r.driver_name) },
    { key: 'car_no', label: '차량', render: (r) => dash(r.car_no) },
    // 지금 적용된 편성 NO(#284) — 편성변경 뒤 회차마다 어느 편성인지.
    { key: 'car_alloc_id', label: '편성 NO', align: 'right', render: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'amt', label: '금액', align: 'right', render: (r) => <b>{money(r.amt)}</b> },
    // 회사 실납부(10-01 미팅 #30) — 이 정산월 공제와 짝인 **다음 달** 회사 납부액. 회계·관리자만(`pays` 가 null 이면 안 보임).
    ...(data?.pays ? [
      { key: 'pay_amt', label: '회사 납부(다음 달)', align: 'right', render: (r: Row) => (r.pay_amt == null ? <span className="text-ink-faint">—</span> : money(r.pay_amt)) },
      { key: '_pd', label: '차이', align: 'right', render: (r: Row) => {
        if (r.pay_amt == null) return '';
        const d = Number(r.pay_amt) - Number(r.amt ?? 0);
        return Math.abs(d) < 1 ? <span className="text-emerald-700">같음</span>
          : <b className={d > 0 ? 'text-red-600' : 'text-amber-700'}>{d > 0 ? '+' : ''}{money(String(d))}</b>;
      } },
    ] as Column[] : []),
    { key: 'comment', label: '비고' },
    { key: 'updated_by', label: '변경', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.updated_by)}</span> },
    ...(canEdit ? [{
      key: '_e', label: '',
      render: (r: Row) => (
        <button className="btn-ghost py-0.5 text-xs"
                onClick={(e) => { e.stopPropagation(); void editAmt(r as unknown as Record<string, unknown>); }}>
          금액수정
        </button>
      ),
    } as Column] : []),
  ];

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-bold">
          할부 계약 #{finInstId}
          {data?.head && (
            <span className="ml-2 font-normal text-ink-soft">
              {dash(data.head.car_no as string)} · {dash(data.head.owner_name as string)} ·
              {' '}{dash(data.head.company as string)}
            </span>
          )}
        </h2>
        {canEdit && data?.head && <button className="btn-ghost py-1 text-xs" onClick={() => setEditOpen(true)}>계약 수정</button>}
        {canEdit && <button className="btn-ghost py-1 text-xs text-red-600" onClick={remove}>계약 삭제</button>}
        <button className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>닫기</button>
      </div>

      {error && <ErrorBox message={error} />}

      {editOpen && data?.head && (
        <Modal width="max-w-4xl">
          <FinInstForm head={data.head} amts={amts}
                       onClose={() => setEditOpen(false)}
                       onSaved={() => { setEditOpen(false); reload(); }} />
        </Modal>
      )}

      {data?.head && (
        <div className="flex flex-wrap gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
          <span>원금 <b>{money(data.head.pr_amount as string)}</b></span>
          <span>분납 <b>{money(data.head.month_amount as string)}</b></span>
          <span>첫/마지막 {money(data.head.first_amount as string)} / {money(data.head.last_amount as string)}</span>
          <span>시작 {dash(data.head.start_drive_mon as string)} · {String(data.head.month_cnt ?? '')}개월</span>
          <span>이율 <b>{rateOf(data.head.interest)}</b></span>
          <span>납입개시 {dash(data.head.startdt as string)} · 대출종료 {dash(data.head.finish_date as string)}</span>
          <span>납부일 <b>{data.head.pay_day ? `매월 ${String(data.head.pay_day)}일` : '—'}</b></span>
          {!!data.head.is_repay && (
            <span className="text-amber-700">중도상환 <b>{dash(String(data.head.repay_date ?? '').trim() || null)}</b></span>
          )}
          <span>회차 <b>{data.amts.length}</b>건 · 할부 총합계 <b>{money(amtTotal)}</b></span>
        </div>
      )}

      {data?.pays && <PaySummary pays={data.pays} canEdit={canEdit} onChanged={() => { setNonce((n) => n + 1); onChanged(); }} />}

      {data && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">상환스케줄표</span>
          {data.files?.length
            ? <AllocFileChips files={data.files} fetchUrl={fetchFinInstFileUrl}
                              onRemove={canEdit ? (fid) => void removeFile(fid) : undefined} />
            : <span className="text-ink-faint">없음</span>}
          {canEdit && (
            <label className={`btn-ghost cursor-pointer py-1 text-xs ${fileBusy ? 'pointer-events-none opacity-50' : ''}`}>
              + 파일 추가
              <input type="file" multiple className="hidden" onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                e.target.value = '';
                void addFiles(picked);
              }} />
            </label>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2">
          <span className="text-sm">
            선택 <b>{sel.length}</b>건
            {sel.length === 0 && <span className="ml-1 text-ink-soft">— 바꿀 회차를 체크하세요</span>}
          </span>
          <button className="btn-ghost py-1 text-xs" disabled={!sel.length}
                  onClick={() => setBulk('amt')}>금액 일괄변경</button>
          <button className="btn-ghost py-1 text-xs" disabled={!sel.length}
                  onClick={() => setBulk('alloc')}>편성변경</button>
          <button className="btn-ghost py-1 text-xs" disabled={!sel.length}
                  onClick={() => setBulk('owner')}>차주변경</button>
          <button className="btn-ghost py-1 text-xs" disabled={!sel.length}
                  onClick={() => setBulk('month')}>정산년월 변경</button>
          <button className="btn-ghost py-1 text-xs" disabled={!sel.length}
                  onClick={() => setBulk('comment')}>비고 변경</button>
        </div>
      )}

      {bulk && (
        <Modal>
          <BulkAmtPanel mode={bulk} ids={sel} onClose={() => setBulk(null)} onDone={reload}
                        picked={amts.filter((r) => selSet.has(Number(r.fin_inst_amt_id)))}
                        onError={setError} />
        </Modal>
      )}

      <div className={dragging ? 'select-none' : ''}>
        <DataTable columns={AMT_COLS} rows={amts} loading={loading} tableId="finInst.amts"
                   total={amts.length ? { count: amts.length, label: '할부 총합계', values: { amt: amtTotal } } : null}
                   onRowMouseDown={canEdit ? rowDown : undefined} onRowMouseEnter={canEdit ? rowEnter : undefined}
                   rowClass={(r) => (selSet.has(Number(r.fin_inst_amt_id)) ? 'bg-sky-50' : '')} />
      </div>

      {data && data.logs.length > 0 && (
        <details className="text-xs text-ink-soft">
          <summary className="cursor-pointer">변경이력 {data.logs.length}건</summary>
          <ul className="mt-2 space-y-1">
            {data.logs.map((l, i) => (
              <li key={i}>
                {dash(l.created_at as string)} · {dash(l.status as string)} ·
                {' '}원금 {money(l.pr_amount as string)} · {String(l.month_cnt ?? '')}개월 ·
                {' '}분납 {money(l.month_amount as string)} · {dash(l.created_by as string)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * 회차 일괄 조작 — 레거시 `FinInstAmtChangeAmt` · `_CarAlloc` · `_Owner` 3화면을 한 패널로.
 *
 * 세 화면 다 "선택한 회차들"을 바꾸는 같은 모양이라 화면을 셋으로 나눌 이유가 없었다.
 * 레거시가 나눈 건 WinForms 라 그런 것이고, 여기서는 무엇을 바꾸는지만 다르다.
 *
 * ⚠️ 차주변경만 **마감월을 막지 않는다**(레거시에 게이트가 없다). 서버가 `closedMonths` 를 돌려주면
 *    바꾼 뒤에 알린다 — 원장은 생성 시점 스냅샷이라 자동으로 안 따라간다.
 */
function BulkAmtPanel({ mode, ids, picked, onClose, onDone, onError }: {
  mode: 'amt' | 'alloc' | 'owner' | 'month' | 'comment';
  ids: number[];
  /** 고른 회차 줄 — 비고 기본값·이름 바꿔 넣기(#510). */
  picked: Row[];
  onClose: () => void;
  onDone: () => void;
  onError: (m: string | null) => void;
}) {
  const [amt, setAmt] = useState('');
  /** 고른 회차 중 **첫 달**을 여기로 옮긴다 — 나머지는 같은 간격으로 따라간다. */
  const [month, setMonth] = useState('');
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [allocId, setAllocId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * 비고(#510 「할부금 승계 시 변경 후 비고도 같이」). 고른 회차 비고가 모두 같으면 그 글자로 채우고
   * 편성·차주변경에서도 기본으로 같이 바꾼다. 서로 다르면 기본은 「안 바꿈」(덮어쓰면 회차마다 다른 비고가 사라진다).
   */
  const firstComment = String(picked[0]?.comment ?? '');
  const sameComment = picked.every((r) => String(r.comment ?? '') === firstComment);
  const [comment, setComment] = useState(sameComment ? firstComment : '');
  const [withComment, setWithComment] = useState(sameComment && firstComment !== '');

  /** 편성을 고르면 비고 안의 옛 차량번호·차주·기사 이름을 새 값으로 바꿔 넣는다(사람이 더 고칠 수 있다). */
  function swapNames(next: { car_no: string | null; owner_name: string | null; driver_name: string | null }) {
    const old = picked[0];
    if (!old) return;
    let s = comment;
    const pairs: Array<[unknown, string | null]> = [
      [old.car_no, next.car_no], [old.owner_name, next.owner_name], [old.driver_name, next.driver_name],
    ];
    for (const [a, b] of pairs) {
      const from = String(a ?? '').trim();
      if (from && b && from !== b) s = s.split(from).join(b);
    }
    setComment(s);
  }

  const title = mode === 'amt' ? '금액 일괄변경'
    : mode === 'alloc' ? '편성변경'
    : mode === 'month' ? '정산년월 변경'
    : mode === 'comment' ? '비고 변경'
    : '차주변경';
  /** 편성·차주변경에 실어 보낼 비고 — 체크 안 하면 undefined(서버가 그대로 둔다). */
  const sendComment = withComment ? comment : undefined;

  async function submit() {
    onError(null);
    setBusy(true);
    try {
      if (mode === 'month') {
        if (!/^\d{4}-\d{2}$/.test(month)) { onError('정산년월을 YYYY-MM 으로 고르세요.'); setBusy(false); return; }
        const r = await api.shiftFinInstAmtMonths({ finInstAmtIds: ids, toDriveMon: month });
        window.alert(
          `회차 ${r.changed}건의 정산년월을 ${r.shift > 0 ? '+' : ''}${r.shift}개월 옮겼습니다.`
          + `
첫 회차 ${r.from} → ${r.to}`,
        );
      } else if (mode === 'amt') {
        const r = await api.bulkUpdateFinInstAmt({ finInstAmtIds: ids, amt });
        window.alert(`회차 ${r.changed}건의 금액을 바꿨습니다.`);
      } else if (mode === 'comment') {
        const r = await api.bulkUpdateFinInstComment({ finInstAmtIds: ids, comment });
        window.alert(`회차 ${r.changed}건의 비고를 바꿨습니다.`);
      } else if (mode === 'alloc') {
        if (!allocId) { onError('편성을 선택하세요.'); setBusy(false); return; }
        const r = await api.changeFinInstAmtCarAlloc({ finInstAmtIds: ids, carAllocId: allocId, comment: sendComment });
        window.alert(`회차 ${r.changed}건의 편성(차량·차주·기사·소속)${sendComment !== undefined ? '과 비고' : ''}를 바꿨습니다.`);
      } else {
        if (!ownerId) { onError('차주를 선택하세요.'); setBusy(false); return; }
        const r = await api.changeFinInstAmtOwner({ finInstAmtIds: ids, ownerId, comment: sendComment });
        window.alert(
          `회차 ${r.changed}건의 차주${sendComment !== undefined ? '와 비고' : ''}를 바꿨습니다.`
          + (r.closedMonths.length
            ? `\n\n⚠️ ${r.closedMonths.join(', ')} 은(는) 이미 기사정산이 마감된 달입니다.`
              + '\n이미 만들어진 명세는 따라 바뀌지 않습니다 — 필요하면 그 달 명세를 다시 만드세요.'
            : ''),
        );
      }
      onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3 border-brand/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold">{title}</h3>
        <span className="text-xs text-ink-soft">선택한 회차 {ids.length}건에 적용합니다.</span>
        <button className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>취소</button>
      </div>

      {mode === 'amt' && (
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="w-20">변경 금액</span>
          <MoneyInput className="w-48" value={amt} placeholder="예: 350,000" onChange={setAmt} />
          <span className="text-xs text-ink-soft">선택한 회차 전부 이 금액이 됩니다.</span>
        </label>
      )}

      {mode === 'month' && (
        <div className="space-y-1">
          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-20">첫 회차를</span>
            <input className="input w-40" value={month} placeholder="YYYY-MM"
                   onChange={(e) => setMonth(e.target.value)} />
            <span className="text-xs text-ink-soft">로 옮깁니다.</span>
          </label>
          <p className="text-xs text-ink-soft">
            고른 회차 중 <b>가장 앞 달</b>을 이 달로 옮기고, <b>나머지는 같은 간격만큼</b> 따라 옮깁니다 —
            납입이 한 달 밀리면 그 뒤가 전부 한 달씩 밀리는 것이 업무입니다.
          </p>
          <p className="text-xs text-amber-700">
            ⚠️ 옮기기 <b>전 달과 옮긴 뒤 달을 둘 다</b> 검사합니다(마감월·지난 달로는 못 옮깁니다).
            같은 계약에서 달이 겹치면 그 달 명세에 두 번 붙으므로 거부합니다.
          </p>
        </div>
      )}

      {mode === 'alloc' && (
        <div className="space-y-1">
          {/* 시작 전 편성도 고른다(#380) — 그 편성 시작월보다 앞 회차가 섞이면 서버가 거부한다 */}
          <AllocPicker includeFuture value={allocId} onPick={setAllocId}
                       onPickRow={(r) => { if (withComment) swapNames(r); }} />
          <p className="text-xs text-ink-soft">
            차량·차주·기사·소속을 <b>네 개 함께</b> 바꿉니다. 하나만 바꾸면 명세에서 엉뚱한 사람에게 붙습니다.
          </p>
        </div>
      )}

      {mode === 'owner' && (
        <div className="space-y-1">
          <RefPicker kind="owner" value={ownerId} onPick={setOwnerId} />
          <p className="text-xs text-ink-soft">
            <b>차주만</b> 바꿉니다(차량·기사·소속은 그대로).
            {' '}이 기능은 마감월도 바꿀 수 있습니다 — 기존 시스템과 같은 동작입니다.
          </p>
        </div>
      )}

      {(mode === 'alloc' || mode === 'owner') && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={withComment} onChange={(e) => setWithComment(e.target.checked)} />
          비고도 같이 바꾸기
          {!sameComment && <span className="text-xs text-amber-700">고른 회차 비고가 서로 다릅니다 — 체크하면 모두 아래 글자가 됩니다.</span>}
        </label>
      )}
      {(mode === 'comment' || ((mode === 'alloc' || mode === 'owner') && withComment)) && (
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="w-20">비고</span>
          <input className="input min-w-[24rem] flex-1" value={comment} maxLength={500}
                 onChange={(e) => setComment(e.target.value)} />
          {mode === 'alloc' && <span className="w-full text-xs text-ink-soft">편성을 고르면 비고 안의 옛 차량번호·차주·기사 이름이 새 것으로 바뀌어 들어갑니다.</span>}
          {mode === 'comment' && !sameComment && <span className="w-full text-xs text-amber-700">고른 회차 비고가 서로 다릅니다 — 모두 이 글자가 됩니다.</span>}
        </label>
      )}

      <button className="btn py-1 text-xs" disabled={busy || !ids.length} onClick={() => void submit()}>
        {busy ? '적용 중…' : `${title} 적용`}
      </button>
    </div>
  );
}

/**
 * 편성 선택 — `RefPicker` 에 편성 종류가 없어 따로 만들었다.
 * ⚠️ **"현재 편성"은 유일하지 않다**(차량 1대당 최대 205건, SPEC §3). 그래서 차량을 고르게 하고
 *    그 차량의 편성을 나열해 사람이 고른다. 자동으로 하나를 집으면 조용히 틀린 편성이 붙는다.
 */
export function AllocPicker({ value, onPick, label, onPickRow, initialTerm, includeFuture, startedBy }: {
  value: number | null; onPick: (id: number | null) => void; label?: string | null;
  /** 고른 편성 줄 전체 — 차주·기사·차량·소속을 같이 채울 때(재무추가 #103). */
  onPickRow?: (r: Awaited<ReturnType<typeof api.listCarAllocs>>['rows'][number]) => void;
  /** 처음 검색어(예: 입금 적요의 번호판 #156) — 있으면 열자마자 찾아 후보를 보인다. 고르기는 사람이 한다. */
  initialTerm?: string | null;
  /**
   * 적용일이 안 된 편성도 보이기 — 편성 변경 화면 · 할부 계약 등록·회차 편성변경(#380) · 정산월 없는 재무추가(#383)만. 그 밖 정산·배차 고르기는
   * 적용일 전 편성을 안 보인다(10-21 way #358 후속 · 서버 저장 가드도 같은 기준).
   */
  includeFuture?: boolean;
  /**
   * 이 날짜(YYYY-MM-DD)까지 시작하는 편성도 보이기 — 정산월 있는 재무추가(#465 송선우 「11월 정산분에 11-01 편성이 안 보인다」).
   * 저장 가드(`saveResultDaily` · 정산월 ≥ 편성 시작월)와 같은 답이 되게 그 정산월 말일을 넘긴다.
   */
  startedBy?: string | null;
}) {
  const [term, setTerm] = useState(initialTerm ?? '');
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.listCarAllocs>>['rows']>([]);
  const [busy, setBusy] = useState(false);

  const [more, setMore] = useState(false);
  const seq = useRef(0);
  async function search(t = term) {
    if (!t.trim()) return;
    const my = ++seq.current;
    setBusy(true);
    // 정산 끝난 편성도 찾는다(#101 — 차량 매각 뒤 예치금 반환처럼 편성이 이미 닫힌 뒤에 오는 공제) · 진행 중을 위로.
    try {
      const all = (await api.listCarAllocs({ q: t.trim(), limit: PICK_LIMIT, scope: 'all', started: !includeFuture, ...(startedBy ? { startedBy } : {}) })).rows;
      if (my !== seq.current) return; // 늦게 온 옛 검색 결과는 버린다
      setRows([...all.filter((r) => r.is_use), ...all.filter((r) => !r.is_use)]);
      setMore(all.length >= PICK_LIMIT);
    }
    finally { if (my === seq.current) setBusy(false); }
  }

  /**
   * 치는 대로 찾는다(10-14 #248 정여진). 「회계계정」만 쳐도 252편성이라 앞 30개에서 잘렸고, 이어서 「_회계전용」까지
   * 친 뒤 검색을 안 누르면 목록이 옛 검색 그대로라 「안 뜬다」로 보였다. 두 글자부터 0.3초 쉬면 찾는다.
   */
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; if (initialTerm?.trim()) void search(initialTerm); return; }
    if (term.trim().length < 2) return;
    const t = setTimeout(() => void search(term), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  // 정산월을 바꾸면 보이는 편성이 달라진다(#465) — 찾아 둔 목록이 있으면 새 기준으로 다시 찾는다.
  const firstBy = useRef(true);
  useEffect(() => {
    if (firstBy.current) { firstBy.current = false; return; }
    if (term.trim()) void search(term);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedBy, includeFuture]);

  const picked = rows.find((r) => r.car_alloc_id === value);
  const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-20 text-sm">편성</span>
        <input className="input w-56" value={term} placeholder="차량번호·차주·기사로 검색"
               onChange={(e) => setTerm(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void search(); } }} />
        <button type="button" className="btn-ghost py-1 text-xs" disabled={busy} onClick={() => void search()}>검색</button>
        {value && (
          <span className="text-sm">
            선택: <b>{picked ? `${dash(picked.car_no)} · ${dash(picked.owner_name)} · ${dash(picked.driver_name)}` : label || `#${value}`}</b>
            {/* 기사 연락처·편성 NO(#242 정여진 — 「고르면 기사님 번호가 옆에」) */}
            {picked && (
              <span className="ml-2 text-ink-soft">
                {picked.driver_phone ? <>기사 연락처 <b className="tabular-nums text-ink">{picked.driver_phone}</b> · </> : null}
                편성 NO {picked.car_alloc_id}
              </span>
            )}
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-lg border border-line text-sm">
          {rows.map((r) => (
            <li key={r.car_alloc_id}>
              <button type="button"
                      className={`w-full px-3 py-1 text-left hover:bg-surface ${r.car_alloc_id === value ? 'bg-surface font-bold' : ''}`}
                      onClick={() => { onPick(r.car_alloc_id); onPickRow?.(r); }}>
                {dash(r.car_no)} · {dash(r.owner_name)} · {dash(r.driver_name)} · {dash(r.grp_name)}
                {r.driver_phone && <span className="ml-1 font-normal tabular-nums text-ink-soft">· {r.driver_phone}</span>}
                <span className="ml-2 text-xs text-ink-soft">
                  {dash(r.startdt)} ~ {r.enddt ? r.enddt : ''}
                </span>
                {/* 지금 살아 있는 편성 = 정산 진행 중 + 종료일 없음/안 지남 — 눈에 띄게 파란 상자(#229) */}
                {r.is_use && (!r.enddt || r.enddt >= today) && !(r.startdt && r.startdt > today) && <span className="ml-2 rounded bg-brand-600 px-1.5 py-0.5 text-xs font-bold text-white">진행중</span>}
                {r.startdt && r.startdt > today && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-bold text-amber-800">{r.startdt.slice(5)}부터</span>}
                {!r.is_use &&<span className="ml-2 rounded bg-surface px-1 text-xs text-ink-soft">정산 종료</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {more && (
        <p className="text-xs text-amber-700">찾은 편성이 많아 앞 {PICK_LIMIT}개만 보입니다 — 차량번호·이름을 더 적으시면 좁혀집니다.</p>
      )}
    </div>
  );
}

/** 편성 찾기 한 번에 보여 줄 줄 수(#248 — 30 에서 잘려 찾는 편성이 안 보였다). */
const PICK_LIMIT = 50;

/** 새 계약 — 저장하면 회차가 함께 전개된다. */
/** 계약 칸 값 → 입력칸 글자(빈값·공백은 ''). */
const s0 = (v: unknown) => (v == null ? '' : String(v).trim());
/** 금액(numeric 문자열 「1234.0000」) → 입력칸 글자. */
const m0 = (v: unknown) => (v == null || String(v).trim() === '' ? '' : moneyInput(v));

/**
 * 할부 계약 등록·수정 창. `head` 가 있으면 수정(#339 — 할부사·금액 오타 · 중도상환날짜 · 납부일).
 * 수정은 회차를 다시 만들지 않는다 — 금액을 바꾸면 서버가 열린 달 회차 중 옛 금액 그대로인 것만 맞춘다.
 */
function FinInstForm({ head, amts, onClose, onSaved }: {
  head?: Record<string, unknown>; amts?: Row[]; onClose: () => void; onSaved: () => void;
}) {
  const editId = head ? Number(head.fin_inst_id) : null;
  // 편성으로 찾기(#230) — 편성 한 줄을 고르면 차량·차주·기사·소속 네 칸이 같이 찬다(아래 네 칸으로 하나씩 고칠 수 있다).
  //   RefPicker 는 고른 이름(label)을 처음 그릴 때만 받으므로 편성을 고를 때마다 key 를 바꿔 다시 그린다.
  const [allocId, setAllocId] = useState<number | null>(null);
  const [pickSeq, setPickSeq] = useState(0);
  const [names, setNames] = useState<{ car?: string; owner?: string; driver?: string; grp?: string }>(head ? {
    car: s0(head.car_no) || undefined, owner: s0(head.owner_name) || undefined,
    driver: s0(head.driver_name) || undefined, grp: s0(head.grp_name) || undefined,
  } : {});
  /** 상환스케줄표(#230) — 「등록」을 누를 때 먼저 올리고 그 번호를 저장에 같이 보낸다. */
  const [files, setFiles] = useState<File[]>([]);
  const idOf = (v: unknown) => (v == null || v === '' ? null : Number(v));
  const [carId, setCarId] = useState<number | null>(idOf(head?.car_id));
  const [ownerId, setOwnerId] = useState<number | null>(idOf(head?.owner_id));
  const [driverId, setDriverId] = useState<number | null>(idOf(head?.driver_id));
  const [grpId, setGrpId] = useState<number | null>(idOf(head?.grp_id));
  const [company, setCompany] = useState(s0(head?.company));
  const [payMethod, setPayMethod] = useState(s0(head?.pay_method) || '자동이체');
  const [startMon, setStartMon] = useState(s0(head?.start_drive_mon));
  const [monthCnt, setMonthCnt] = useState(s0(head?.month_cnt));
  // #371 — 중도변경으로 새로 넣는 계약: 앞 회차(정산이 끝난 달)는 건너뛰고 N회차부터. 새 등록에서만.
  const [startSeq, setStartSeq] = useState('1');
  const seqN = Math.max(1, Number(startSeq) || 1);
  const [prAmount, setPrAmount] = useState(m0(head?.pr_amount));
  const [monthAmount, setMonthAmount] = useState(m0(head?.month_amount));
  const [firstAmount, setFirstAmount] = useState(m0(head?.first_amount));
  const [lastAmount, setLastAmount] = useState(m0(head?.last_amount));
  // #254 — 레거시 계약 창과 같은 칸(납입개시일·대출종료일·이율).
  // ⚠️ 이관 값엔 날짜 꼴이 아닌 글자가 있다 — 날짜 칸에 넣으면 비어 보이고 저장 때 지워진다(issues 106)
  //    → 수정에선 바꾼 칸만 보낸다(서버는 안 보낸 칸을 그대로 둔다).
  const iso = (v: unknown) => (/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s0(v)) ? s0(v) : '');
  const [startdt, setStartdt] = useState(iso(head?.startdt));
  const [finishDate, setFinishDate] = useState(iso(head?.finish_date));
  const [interest, setInterest] = useState(s0(head?.interest));
  const [comment, setComment] = useState(s0(head?.comment));
  // #339 — 할부 납부일(매월 1~31일) · 중도상환날짜(수정에서만).
  const [payDay, setPayDay] = useState(s0(head?.pay_day));
  const [repayDate, setRepayDate] = useState(head?.is_repay ? iso(head?.repay_date) : '');
  const init = useRef({ startdt, finishDate, interest, repayDate });
  /** 중도상환날짜 그 달 **뒤** 회차 — 저장하면 내려간다(마감·지난 달은 서버가 그대로 둔다). */
  const repayMon = repayDate.slice(0, 7);
  const afterRepay = repayMon ? (amts ?? []).filter((a) => String(a.drive_mon ?? '') > repayMon) : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickAlloc(r: Awaited<ReturnType<typeof api.listCarAllocs>>['rows'][number]) {
    setCarId(r.car_id ?? null);
    setOwnerId(r.owner_id ?? null);
    setDriverId(r.driver_id ?? null);
    setGrpId(r.grp_id ?? null);
    setNames({
      car: r.car_no ?? undefined, owner: r.owner_name ?? undefined,
      driver: r.driver_name ?? undefined, grp: r.grp_name ?? undefined,
    });
    setPickSeq((n) => n + 1);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (editId !== null && repayDate && repayDate !== init.current.repayDate && afterRepay.length
        && !window.confirm(`중도상환날짜 ${repayDate} 뒤 회차 ${afterRepay.length}건(${String(afterRepay[0]?.drive_mon ?? '')}~)은 공제에서 내려갑니다.\n(정산이 끝난 달은 그대로 둡니다 · 날짜를 지우면 되살아납니다)\n계속할까요?`)) return;
    setBusy(true); setError(null);
    try {
      // 상환스케줄표(#230) — 먼저 올려(대기) 번호를 받고, 저장이 같은 트랜잭션에서 이 계약에 붙인다.
      const fileIds: number[] = [];
      for (const f of files) {
        try { fileIds.push((await uploadFinInstFile(f)).fileId); }
        catch (err) { throw new Error(`상환스케줄표 「${f.name}」을 올리지 못했습니다 — ${err instanceof Error ? err.message : String(err)}`); }
      }
      const args: Record<string, unknown> = {
        carId: carId ?? undefined, ownerId: ownerId ?? undefined,
        driverId: driverId ?? undefined, grpId: grpId ?? undefined,
        company, payMethod, startDriveMon: startMon, monthCnt: Number(monthCnt),
        prAmount, monthAmount, firstAmount: firstAmount || undefined, lastAmount: lastAmount || undefined,
        payDay: payDay ? Number(payDay) : null,
        comment: comment || undefined,
        fileIds: fileIds.length ? fileIds : undefined,
      };
      if (editId === null) {
        Object.assign(args, { startdt: startdt || null, finishDate: finishDate || null, interest: interest || null });
        if (seqN > 1) args.startSeq = seqN;
      } else {
        args.finInstId = editId;
        const i0 = init.current;
        if (startdt !== i0.startdt) args.startdt = startdt || null;
        if (finishDate !== i0.finishDate) args.finishDate = finishDate || null;
        if (interest !== i0.interest) args.interest = interest || null;
        if (repayDate !== i0.repayDate) args.repayDate = repayDate || null;
      }
      const r = await api.saveFinInst(args);
      if (editId === null) {
        window.alert(`계약 #${r.finInstId} 등록. 회차 ${r.created}건이 만들어졌습니다.${r.files ? ` 상환스케줄표 ${r.files}개를 붙였습니다.` : ''}`);
      } else {
        const notes = [
          r.amtSynced ? `회차 ${r.amtSynced}건 금액을 새 금액으로 맞췄습니다.` : '',
          r.repayCut ? `중도상환일 뒤 회차 ${r.repayCut}건을 내렸습니다.` : '',
          r.repayRestored ? `내렸던 회차 ${r.repayRestored}건을 되살렸습니다.` : '',
          r.files ? `상환스케줄표 ${r.files}개를 붙였습니다.` : '',
        ].filter(Boolean);
        window.alert(`계약 #${r.finInstId} 을 고쳤습니다.${notes.length ? `\n${notes.join('\n')}` : ''}`);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card space-y-3 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-bold">{editId === null ? '새 할부 계약' : `할부 계약 수정 #${editId}`}</h2>
        <button type="button" className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>닫기</button>
      </div>
      {error && <ErrorBox message={error} />}

      <div className="rounded-lg border border-line bg-surface p-3">
        <p className="mb-2 text-xs text-ink-soft">편성으로 찾기 — 차량번호·차주·기사 이름으로 찾아 고르면 아래 네 칸이 한 번에 채워집니다.</p>
        {/* 새로 들어올 차량 할부는 적용일 전에 넣는다(#380) — 시작월보다 앞 회차가 있으면 서버가 거부 */}
        <AllocPicker includeFuture value={allocId} onPick={setAllocId} onPickRow={pickAlloc} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1">
          <span className="text-xs text-ink-soft">차량</span>
          <RefPicker key={`car-${pickSeq}`} kind="car" value={carId} label={names.car} onPick={setCarId} />
        </div>
        <div className="space-y-1">
          <span className="text-xs text-ink-soft">차주</span>
          <RefPicker key={`owner-${pickSeq}`} kind="owner" value={ownerId} label={names.owner} onPick={setOwnerId} />
        </div>
        <div className="space-y-1">
          <span className="text-xs text-ink-soft">기사</span>
          <RefPicker key={`driver-${pickSeq}`} kind="driver" value={driverId} label={names.driver} onPick={setDriverId} />
        </div>
        <div className="space-y-1">
          <span className="text-xs text-ink-soft">소속</span>
          <RefPicker key={`grp-${pickSeq}`} kind="grp" value={grpId} label={names.grp} onPick={setGrpId} />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">할부금계정</span>
          <input className="field w-full" value={company} onChange={(e) => setCompany(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">납부방법</span>
          <select className="field w-full" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
            <option>자동이체</option>
            <option>통장입금</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">{editId === null && seqN > 1 ? `${seqN}회차 정산월` : '납부시작월'}</span>
          <input className="field w-full" placeholder="2026-08" value={startMon}
                 onChange={(e) => setStartMon(e.target.value)} required />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">상환월수</span>
          <input className="field w-full text-right" value={monthCnt}
                 onChange={(e) => setMonthCnt(e.target.value)} required />
        </label>
        {editId === null && (
          <label className="space-y-1">
            <span className="text-xs text-ink-soft">시작 회차(중도변경)</span>
            <input className="field w-full text-right" inputMode="numeric" value={startSeq}
                   onChange={(e) => setStartSeq(e.target.value.replace(/[^0-9]/g, ''))} />
          </label>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">대출원금</span>
          <MoneyInput className="w-full" value={prAmount} onChange={setPrAmount} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">분납액</span>
          <MoneyInput className="w-full" value={monthAmount} onChange={setMonthAmount} required />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">첫 회차(비우면 분납액)</span>
          <MoneyInput className="w-full" value={firstAmount} onChange={setFirstAmount} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">마지막 회차(비우면 분납액)</span>
          <MoneyInput className="w-full" value={lastAmount} onChange={setLastAmount} />
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">납입개시일</span>
          <input type="date" className="field w-full" value={startdt} onChange={(e) => setStartdt(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">대출종료일</span>
          <input type="date" className="field w-full" value={finishDate} onChange={(e) => setFinishDate(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">이율(%)</span>
          <input className="field w-full text-right" inputMode="decimal" placeholder="예: 7.1" value={interest}
                 onChange={(e) => setInterest(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">할부 납부일</span>
          <select className="field w-full" value={payDay} onChange={(e) => setPayDay(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => <option key={d} value={d}>{d}일</option>)}
          </select>
        </label>
        {editId !== null && (
          <label className="space-y-1">
            <span className="text-xs text-ink-soft">중도상환날짜</span>
            <input type="date" className="field w-full" value={repayDate} onChange={(e) => setRepayDate(e.target.value)} />
          </label>
        )}
      </div>
      {editId !== null && repayDate && (
        <p className="text-xs text-amber-700">
          중도상환으로 정리합니다 — {repayDate.slice(0, 7)} 뒤 회차 {afterRepay.length}건은 공제에서 내려갑니다(날짜를 지우면 되살아납니다).
          {' '}이어진 회사 실납부도 같은 날짜로 끝납니다.
        </p>
      )}

      <label className="block space-y-1">
        <span className="text-xs text-ink-soft">비고</span>
        <input className="field w-full" value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="btn-ghost cursor-pointer">
          📎 상환스케줄표 올리기
          <input type="file" multiple className="hidden" onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            setFiles((cur) => [...cur, ...picked].slice(0, 10));
          }} />
        </label>
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1 text-xs">
            <span className="max-w-[16rem] truncate">{f.name}</span>
            <button type="button" className="text-ink-faint hover:text-red-600" title="빼기" onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}>×</button>
          </span>
        ))}
        {files.length === 0 && <span className="text-xs text-ink-faint">사진·PDF·엑셀 등 (한 파일 20MB까지) — 등록할 때 같이 올라갑니다</span>}
      </div>

      {editId === null ? (
        <p className="text-xs text-ink-faint">
          {seqN > 1
            ? <>저장하면 <b>{seqN}회차</b>부터 <b>{monthCnt || 'N'}회차</b>까지 회차가 {startMon || '정산월'}부터 만들어집니다(1~{seqN - 1}회차는 만들지 않습니다).</>
            : <>저장하면 <b>{monthCnt || 'N'}</b>개월치 회차가 {startMon || '시작월'}부터 만들어집니다.</>}
          정산이 완료된 달이 범위에 들어가면 저장이 거부됩니다 — 중도변경으로 앞 회차가 이미 정산된 계약은 「시작 회차」에 이어 갈 회차를 넣으세요.
        </p>
      ) : (
        <p className="text-xs text-ink-faint">
          회차는 다시 만들지 않습니다. 분납액·첫/마지막 회차 금액을 바꾸면 정산이 끝나지 않은 달의 회차 중
          옛 금액 그대로인 회차만 새 금액으로 바뀝니다(따로 금액수정한 회차는 그대로).
        </p>
      )}

      <button className="btn" disabled={busy}>{busy ? '저장 중…' : editId === null ? '등록' : '저장'}</button>
    </form>
  );
}

type PickedAlloc = Awaited<ReturnType<typeof api.listCarAllocs>>['rows'][number];

/** 붙여 넣은 칸 → 금액 글자(쉼표·원·공백 제거). 금액 꼴이 아니면 null. */
const amtCell = (v: string | undefined): string | null => {
  const s = (v ?? '').replace(/[,원 ]/g, '').trim();
  return /^[0-9]+([.][0-9]+)?$/.test(s) ? s : null;
};
/** 표 아래 합계 줄(「합계」·「총계」·「계」)은 회차가 아니다. */
const isTotalRow = (cells: string[]) => cells.some((c) => /^(합계|총계|계|소계)$/.test(c.replace(/ /g, '')));

/**
 * 상환스케줄 붙여넣기 해석 — 엑셀에서 표를 통째로 복사해도, 금액 한 열만 복사해도 된다.
 * 머리줄이 있으면 「합계」 열을 먼저 고르고(캐피탈 스케줄 = 원금+이자 합계가 공제액 · #43), 없으면 첫 금액 열.
 */
function parseSchedule(textIn: string, col: number | null) {
  const lines = textIn.split(/\r?\n/).map((l) => l.split('\t').map((c) => c.trim())).filter((c) => c.some((x) => x !== ''));
  const header = lines.length && lines[0]!.every((c) => c === '' || amtCell(c) === null) ? lines[0]! : null;
  const body = (header ? lines.slice(1) : lines).filter((c) => !isTotalRow(c));
  const width = Math.max(0, ...lines.map((c) => c.length));
  const names = Array.from({ length: width }, (_, i) => (header?.[i] || `${i + 1}열`));
  const auto = (() => {
    const hit = header ? header.findIndex((h) => h.replace(/ /g, '').includes('합계')) : -1;
    if (hit >= 0) return hit;
    for (let i = 0; i < width; i++) if (body.length && body.every((c) => amtCell(c[i]) !== null)) return i;
    return 0;
  })();
  const use = col !== null && col < width ? col : auto;
  const amts: string[] = []; const badRows: number[] = [];
  body.forEach((c, i) => { const a = amtCell(c[use]); if (a === null) badRows.push(i + 1); else amts.push(a); });
  return { names, use, amts, badRows };
}

const addMon = (mon: string, n: number) => {
  const [y, m] = mon.split('-').map(Number);
  const t = y! * 12 + (m! - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
};

/**
 * 할부 대량 등록(#494 정여진) — 같은 조건으로 들이는 차량 여러 대에 캐피탈 상환스케줄의 회차별 금액 그대로 계약을 건다.
 * 「새 계약」은 첫·분납·마지막 세 금액뿐이라 이자가 달마다 다른 스케줄을 못 넣는다. 저장은 전부 아니면 전무(서버 한 트랜잭션).
 */
function FinInstBulkForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [allocs, setAllocs] = useState<PickedAlloc[]>([]);
  const [company, setCompany] = useState('');
  const [payMethod, setPayMethod] = useState('자동이체');
  const [startMon, setStartMon] = useState('');
  const [prAmount, setPrAmount] = useState('');
  const [startdt, setStartdt] = useState('');
  const [finishDate, setFinishDate] = useState('');
  const [interest, setInterest] = useState('');
  const [payDay, setPayDay] = useState('');
  const [comment, setComment] = useState('');
  const [paste, setPaste] = useState('');
  const [col, setCol] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sched = useMemo(() => parseSchedule(paste, col), [paste, col]);
  const total = sched.amts.reduce((s, a) => s + Number(a), 0);
  const monOk = /^[0-9]{4}-[0-9]{2}$/.test(startMon.trim());

  function add(r: PickedAlloc) {
    setAllocs((cur) => (cur.some((x) => x.car_alloc_id === r.car_alloc_id) ? cur : [...cur, r]));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!allocs.length) { setError('편성을 하나 이상 고르세요.'); return; }
    if (sched.badRows.length) { setError(`상환스케줄 ${sched.badRows.slice(0, 5).join(', ')}번째 줄이 금액이 아닙니다.`); return; }
    if (!window.confirm(`${allocs.length}대에 계약을 하나씩 등록합니다.\n회차 ${sched.amts.length}건(${startMon.trim()}~${monOk ? addMon(startMon.trim(), sched.amts.length - 1) : ''}) · 한 대 합계 ${money(String(total))}원\n계속할까요?`)) return;
    setBusy(true); setError(null);
    try {
      const r = await api.bulkCreateFinInst({
        allocIds: allocs.map((a) => a.car_alloc_id), schedule: sched.amts,
        company, payMethod, startDriveMon: startMon.trim(), prAmount: prAmount || undefined,
        payDay: payDay ? Number(payDay) : null, startdt: startdt || null, finishDate: finishDate || null,
        interest: interest || null, comment: comment || undefined,
      });
      window.alert(`${r.contracts.length}대 등록 — ${r.contracts.map((c) => `${c.carNo} #${c.finInstId}`).join(', ')}\n회차 ${r.months}건씩(${r.from}~${r.to}) 만들어졌습니다.`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card space-y-3 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-bold">할부 대량 등록</h2>
        <button type="button" className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>닫기</button>
      </div>
      {error && <ErrorBox message={error} />}

      <div className="rounded-lg border border-line bg-surface p-3">
        <p className="mb-2 text-xs text-ink-soft">같은 조건으로 등록할 편성을 찾아 하나씩 고르세요 — 고른 편성마다 계약이 하나씩 만들어집니다.</p>
        <AllocPicker includeFuture value={null} onPick={() => undefined} onPickRow={add} />
        {allocs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {allocs.map((a) => (
              <span key={a.car_alloc_id} className="inline-flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1 text-xs">
                <b>{dash(a.car_no)}</b> · {dash(a.owner_name)} · {dash(a.driver_name)} <span className="text-ink-soft">편성 NO {a.car_alloc_id}</span>
                <button type="button" className="text-ink-faint hover:text-red-600" title="빼기"
                        onClick={() => setAllocs((cur) => cur.filter((x) => x.car_alloc_id !== a.car_alloc_id))}>×</button>
              </span>
            ))}
            <span className="self-center text-xs text-ink-soft">{allocs.length}대</span>
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">할부금계정</span>
          <input className="field w-full" value={company} onChange={(e) => setCompany(e.target.value)} required />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">납부방법</span>
          <select className="field w-full" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
            <option>자동이체</option>
            <option>통장입금</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">납부시작월(1회차 정산월)</span>
          <input className="field w-full" placeholder="2026-11" value={startMon} onChange={(e) => setStartMon(e.target.value)} required />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">대출원금(한 대)</span>
          <MoneyInput className="w-full" value={prAmount} onChange={setPrAmount} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">납입개시일</span>
          <input type="date" className="field w-full" value={startdt} onChange={(e) => setStartdt(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">대출종료일</span>
          <input type="date" className="field w-full" value={finishDate} onChange={(e) => setFinishDate(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">이율(%)</span>
          <input className="field w-full text-right" inputMode="decimal" placeholder="예: 3.3" value={interest}
                 onChange={(e) => setInterest(e.target.value)} />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">할부 납부일</span>
          <select className="field w-full" value={payDay} onChange={(e) => setPayDay(e.target.value)}>
            <option value="">—</option>
            {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => <option key={d} value={d}>{d}일</option>)}
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs text-ink-soft">비고</span>
        <input className="field w-full" value={comment} onChange={(e) => setComment(e.target.value)} />
      </label>

      <div className="space-y-2">
        <span className="text-xs text-ink-soft">
          상환스케줄 — 캐피탈 상환스케줄 엑셀에서 표를 복사해 붙여 넣으세요(머리줄 포함 · 금액 열만 복사해도 됩니다). 한 줄이 한 회차입니다.
        </span>
        <textarea className="field h-40 w-full font-mono text-xs" value={paste}
                  placeholder={'회차\t원금\t이자\t합계\n1\t3,000,000\t550,000\t3,550,000\n…'}
                  onChange={(e) => { setPaste(e.target.value); setCol(null); }} />
        {sched.names.length > 1 && (
          <label className="flex items-center gap-2 text-xs">
            <span className="text-ink-soft">회차 금액으로 쓸 열</span>
            <select className="field py-1 text-xs" value={sched.use} onChange={(e) => setCol(Number(e.target.value))}>
              {sched.names.map((n, i) => <option key={i} value={i}>{n}</option>)}
            </select>
          </label>
        )}
        {sched.amts.length > 0 && (
          <p className="text-xs">
            회차 <b>{sched.amts.length}</b>건
            {monOk && <> · {startMon.trim()} ~ {addMon(startMon.trim(), sched.amts.length - 1)}</>}
            {' '}· 1회차 <b>{money(sched.amts[0])}</b> · 2회차 {money(sched.amts[Math.min(1, sched.amts.length - 1)])}
            {' '}· 마지막 {money(sched.amts[sched.amts.length - 1])} · 한 대 합계 <b>{money(String(total))}</b>원
            {allocs.length > 1 && <> · {allocs.length}대 합계 {money(String(total * allocs.length))}원</>}
          </p>
        )}
        {sched.badRows.length > 0 && (
          <p className="text-xs text-red-700">금액이 아닌 줄: {sched.badRows.slice(0, 10).join(', ')}번째 — 열을 바꾸거나 그 줄을 지워 주세요.</p>
        )}
      </div>

      <p className="text-xs text-ink-faint">
        고른 편성마다 회차가 위 금액 그대로 만들어집니다. 정산이 끝난 달이 들어가거나 같은 차량에 같은 납부시작월 계약이 이미 있으면 한 대도 등록하지 않고 알려 드립니다.
        상환스케줄표 파일은 등록 뒤 계약 상세에서 올릴 수 있습니다.
      </p>

      <button className="btn" disabled={busy || !allocs.length || !sched.amts.length}>{busy ? '등록 중…' : `${allocs.length}대 등록`}</button>
    </form>
  );
}
